/**
 * AgenteLead Search OS — Legacy Scraper Retirement (Iter204 · Phase 5).
 *
 * Toggle-based cost-saver: once AI Search coverage passes the tenant's
 * confidence threshold and minimum company count, the operator can flip
 * off Apollo / Hunter / Apify to stop the monthly SaaS spend.
 *
 * Guard: any attempt to DISABLE a provider before coverage is unlocked
 * returns 409 with a PT-BR blocking reason. Turning providers back ON
 * is always allowed (rollback path).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldOff, Loader2, ArrowLeft, DollarSign, Activity,
  TrendingUp, AlertTriangle, CheckCircle2, RefreshCw,
  Lock, Sparkles, History, Mail, Slack, Send, Plus, Trash2,
  Sliders, Save,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";
const PROVIDERS = [
  { key: "apollo", name: "Apollo.io",   note: "Enriquecimento de contatos B2B" },
  { key: "hunter", name: "Hunter.io",   note: "Email finder + domain search" },
  { key: "apify",  name: "Apify",       note: "Web scraping fallback" },
];

export default function SmithAISearchSettings() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("toggles");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [audit, setAudit] = useState([]);

  const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        axios.get(`${API}/ai-search/settings`, authHeader),
        axios.get(`${API}/ai-search/settings/audit?limit=10`, authHeader),
      ]);
      setPayload(r.data);
      setAudit(a.data?.audit || []);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao carregar settings");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line

  const patch = async (key, body, label) => {
    setSaving(key);
    try {
      const r = await axios.put(`${API}/ai-search/settings`, body, authHeader);
      setPayload((prev) => ({ ...prev, settings: r.data.settings, cost: r.data.cost || prev.cost }));
      toast.success(`${label} atualizado`);
      load();
    } catch (ex) {
      const msg = ex?.response?.data?.message || ex?.response?.data?.detail || `Falha ao alterar ${label}`;
      toast.error(msg);
    } finally { setSaving(null); }
  };

  if (loading || !payload) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: NAVY }}>
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  const s = payload.settings || {};
  const cov = payload.coverage || {};
  const cost = payload.cost || {};
  const unlocked = cov.unlock_ready;

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-ai-settings-header"
          title="Legacy Scraper Retirement"
          subtitle="Desligue Apollo/Hunter/Apify quando o AI Search cobrir seu pipeline"
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#0891B2 0%,#164E63 100%)", color: "white" }}>
              <ShieldOff className="w-5 h-5" />
            </div>
          }
        >
          <button type="button" onClick={() => navigate("/smith/ai-search")}
            data-testid="ai-settings-back-btn"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-purple-500/60 inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> AI Lead Search
          </button>
        </AppHeader>

        <main className="max-w-[1200px] mx-auto px-4 lg:px-6 py-6 space-y-6">
          {/* Tab bar */}
          <nav className="flex gap-1 border-b border-white/10" data-testid="ai-settings-tabs">
            {[
              { id: "toggles", label: "🛡 Scraper Retirement" },
              { id: "digest",  label: "📨 Weekly Digest" },
              { id: "icp",     label: "🎯 ICP Profiles" },
            ].map((t) => (
              <button key={t.id} type="button" onClick={() => setTab(t.id)}
                data-testid={`ai-settings-tab-${t.id}`}
                className={`h-9 px-3 text-[12px] font-bold border-b-2 -mb-px ${
                  tab === t.id ? "border-purple-500 text-white"
                    : "border-transparent text-slate-400 hover:text-white"}`}>
                {t.label}
              </button>
            ))}
          </nav>

          {tab === "toggles" && <>
          {/* Coverage hero */}
          <section data-testid="ai-settings-coverage"
            className="rounded-2xl border border-white/10 p-5"
            style={{ background: `linear-gradient(135deg,${unlocked ? "rgba(16,185,129,0.10)" : "rgba(244,63,94,0.06)"} 0%,rgba(15,23,42,0.4) 100%)` }}>
            <div className="flex items-start gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                   style={{ background: unlocked ? "#10B981" : "#334155", color: "white" }}>
                {unlocked ? <CheckCircle2 className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-bold text-white">
                  {unlocked
                    ? "Coverage suficiente — toggles desbloqueados"
                    : "Toggles bloqueados — coverage insuficiente"}
                </div>
                {cov.blocking_reason && (
                  <div className="text-[12px] text-amber-200 mt-1" data-testid="ai-settings-blocking-reason">
                    {cov.blocking_reason}
                  </div>
                )}
              </div>
              <button type="button" onClick={load}
                data-testid="ai-settings-refresh-btn"
                className="h-8 px-2.5 rounded border border-white/15 text-[11px] font-bold text-slate-300 hover:border-purple-500/60 inline-flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3" /> Recarregar
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 pt-4 border-t border-white/5">
              <Metric label="Empresas AI" v={cov.ai_companies_total} sub={`≥${cov.threshold}% conf: ${cov.ai_companies_verified}`} icon={Activity} />
              <Metric label="Confiança média" v={`${cov.avg_confidence}%`} sub={`threshold ${cov.threshold}%`} icon={TrendingUp} />
              <Metric label="Signals" v={cov.signals_count} icon={Sparkles} />
              <Metric label="Contatos" v={cov.contacts_count} icon={Sparkles} />
            </div>
          </section>

          {/* Cost projection */}
          <section data-testid="ai-settings-cost"
            className="rounded-2xl border border-white/10 p-4 flex items-center gap-4 flex-wrap">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)", color: "white" }}>
              <DollarSign className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold">Custo mensal atual</div>
              <div className="text-[20px] font-bold text-white tabular-nums" data-testid="ai-settings-active-cost">
                ${cost.active_monthly_usd || 0}<span className="text-slate-500 text-[13px]">/mo</span>
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold">Economia potencial</div>
              <div className="text-[20px] font-bold text-emerald-300 tabular-nums" data-testid="ai-settings-savings">
                ${cost.potential_savings_usd || 0}<span className="text-slate-500 text-[13px]">/mo</span>
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold">Máximo se tudo ligado</div>
              <div className="text-[20px] font-bold text-slate-300 tabular-nums">
                ${cost.max_monthly_usd || 0}<span className="text-slate-500 text-[13px]">/mo</span>
              </div>
            </div>
          </section>

          {/* Provider toggles */}
          <section data-testid="ai-settings-toggles" className="space-y-3">
            <h2 className="text-[13px] uppercase tracking-widest font-bold text-slate-200">Provedores legados</h2>
            {PROVIDERS.map((p) => {
              const on = !!s[`${p.key}_enabled`];
              const canDisable = unlocked;
              const isSaving = saving === p.key;
              const disabled = isSaving || (on && !canDisable);
              return (
                <div key={p.key}
                  data-testid={`ai-settings-provider-${p.key}`}
                  className="rounded-2xl border border-white/10 p-4 flex items-center gap-4 flex-wrap">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    on ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-500/20 text-slate-400"}`}>
                    {on ? <Activity className="w-5 h-5" /> : <ShieldOff className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-[14px] font-bold text-white">{p.name}</div>
                    <div className="text-[11px] text-slate-400">{p.note}</div>
                    <div className="text-[10.5px] text-slate-500 mt-1">
                      ${s.monthly_cost_estimate?.[p.key] || 0}/mês estimado
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-bold ${on ? "text-emerald-300" : "text-slate-500"}`}>
                      {on ? "ATIVO" : "DESLIGADO"}
                    </span>
                    <button type="button"
                      disabled={disabled}
                      onClick={() => patch(p.key, { [`${p.key}_enabled`]: !on }, p.name)}
                      data-testid={`ai-settings-toggle-${p.key}`}
                      title={on && !canDisable ? "Coverage insuficiente para desligar" : ""}
                      className={`relative h-7 w-14 rounded-full border transition-colors ${
                        on ? "bg-emerald-500/40 border-emerald-500/60" : "bg-slate-700/60 border-slate-600"
                      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-md transition-transform ${
                        on ? "translate-x-8" : "translate-x-0.5"}`} />
                      {isSaving && <Loader2 className="absolute inset-0 m-auto w-3 h-3 animate-spin text-white" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </section>

          {/* Thresholds */}
          <section data-testid="ai-settings-thresholds"
            className="rounded-2xl border border-white/10 p-4 space-y-3">
            <h3 className="text-[12px] uppercase tracking-widest font-bold text-slate-300">Regras de desbloqueio</h3>
            <ThresholdRow label="Confidence threshold (média mínima)" value={s.coverage_threshold}
              suffix="%" min={30} max={95} step={5}
              testid="ai-settings-threshold-input"
              onSave={(v) => patch("coverage_threshold", { coverage_threshold: v }, "Threshold")} />
            <ThresholdRow label="Empresas AI mínimas (min_ai_companies)" value={s.min_ai_companies}
              suffix="" min={5} max={500} step={5}
              testid="ai-settings-min-companies-input"
              onSave={(v) => patch("min_ai_companies", { min_ai_companies: v }, "Min empresas")} />
          </section>

          {/* Audit log */}
          {audit.length > 0 && (
            <section data-testid="ai-settings-audit" className="space-y-3">
              <h2 className="text-[13px] uppercase tracking-widest font-bold text-slate-200 flex items-center gap-2">
                <History className="w-3.5 h-3.5" /> Auditoria de flips
              </h2>
              <div className="rounded-2xl border border-white/10 divide-y divide-white/5">
                {audit.map((a, i) => (
                  <div key={i} className="p-3 text-[11.5px] flex items-start gap-3">
                    <div className="text-slate-500 shrink-0">{new Date(a.updated_at).toLocaleString()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-200">{a.updated_by || "system"}</div>
                      <div className="text-slate-400 truncate">{JSON.stringify(a.patch)}</div>
                    </div>
                    <div className="text-slate-500 shrink-0">
                      cov {a.coverage_snapshot?.avg_confidence}% · {a.coverage_snapshot?.ai_companies_total} cos
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          </>}

          {tab === "digest" && <DigestTab payload={payload} onSaved={load} />}
          {tab === "icp" && <IcpTab />}
        </main>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, v, sub }) {
  return (
    <div className="rounded-lg border border-white/10 p-2.5 flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-400" />
      <div>
        <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="text-[14px] font-bold text-white tabular-nums">{v ?? "—"}</div>
        {sub && <div className="text-[9.5px] text-slate-500">{sub}</div>}
      </div>
    </div>
  );
}

function ThresholdRow({ label, value, min, max, step, suffix, testid, onSave }) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="text-[12px] text-slate-300 flex-1 min-w-[200px]">{label}</div>
      <input type="range" min={min} max={max} step={step} value={local}
        onChange={(e) => setLocal(Number(e.target.value))}
        data-testid={testid}
        className="flex-1 min-w-[160px] accent-purple-500" />
      <span className="text-[13px] font-bold text-white w-14 text-right tabular-nums">{local}{suffix}</span>
      <button type="button"
        onClick={() => onSave(local)} disabled={local === value}
        className="h-8 px-3 rounded border border-white/15 text-[11px] font-bold text-slate-200 hover:border-purple-500/60 disabled:opacity-40 disabled:cursor-not-allowed">
        Salvar
      </button>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  Digest tab
// ═════════════════════════════════════════════════════════════════════════

function DigestTab({ payload, onSaved }) {
  const s = payload?.settings || {};
  const digest = s.digest || {};
  const [enabled, setEnabled] = useState(digest.enabled !== false);
  const [windowDays, setWindowDays] = useState(digest.window_days || 7);
  const [topN, setTopN] = useState(digest.top_n || 10);
  const [slackWebhook, setSlackWebhook] = useState(digest.slack_webhook_url || "");
  const [emails, setEmails] = useState((digest.email_recipients || []).join(", "));
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };

  useEffect(() => {
    axios.get(`${API}/ai-search/digest/preview?days=${windowDays}`, authHeader)
      .then((r) => setPreview(r.data))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        enabled, window_days: windowDays, top_n: topN,
        slack_webhook_url: slackWebhook,
        email_recipients: emails.split(",").map((e) => e.trim()).filter(Boolean),
      };
      await axios.put(`${API}/ai-search/digest/settings`, body, authHeader);
      toast.success("Digest atualizado");
      onSaved?.();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao salvar digest");
    } finally { setSaving(false); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/ai-search/digest/send-now`, {}, { ...authHeader, timeout: 60000 });
      const report = r.data?.report || {};
      const anyOk = Object.values(report).some((v) => v?.ok);
      if (anyOk) toast.success("Digest enviado com sucesso");
      else toast.error("Envio falhou — verifique Slack webhook / Resend key");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha no envio");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="ai-settings-digest">
      <section className="rounded-2xl border border-white/10 p-4 space-y-3">
        <h3 className="text-[12px] uppercase tracking-widest font-bold text-slate-300 flex items-center gap-2">
          <Send className="w-3.5 h-3.5" /> Weekly Digest (Domingo 09:00 UTC)
        </h3>
        <label className="flex items-center gap-2 text-[12px] text-slate-200">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
            data-testid="ai-settings-digest-enabled" />
          Enviar automaticamente todo domingo
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-[11.5px] text-slate-300 block">Janela (dias)
            <input type="number" min={1} max={30} value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
              data-testid="ai-settings-digest-window"
              className="mt-1 h-9 px-2 rounded border border-white/15 bg-black/30 text-white w-full" />
          </label>
          <label className="text-[11.5px] text-slate-300 block">Top-N leads
            <input type="number" min={3} max={50} value={topN}
              onChange={(e) => setTopN(Number(e.target.value))}
              data-testid="ai-settings-digest-topn"
              className="mt-1 h-9 px-2 rounded border border-white/15 bg-black/30 text-white w-full" />
          </label>
        </div>
        <label className="text-[11.5px] text-slate-300 block"><Slack className="w-3 h-3 inline mr-1" /> Slack Webhook URL
          <input type="url" value={slackWebhook} onChange={(e) => setSlackWebhook(e.target.value)}
            data-testid="ai-settings-digest-slack"
            placeholder="https://hooks.slack.com/services/..."
            className="mt-1 h-9 px-2 rounded border border-white/15 bg-black/30 text-white w-full" />
        </label>
        <label className="text-[11.5px] text-slate-300 block"><Mail className="w-3 h-3 inline mr-1" /> Emails (separados por vírgula)
          <input type="text" value={emails} onChange={(e) => setEmails(e.target.value)}
            data-testid="ai-settings-digest-emails"
            placeholder="ceo@empresa.com, sdr@empresa.com"
            className="mt-1 h-9 px-2 rounded border border-white/15 bg-black/30 text-white w-full" />
        </label>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={save} disabled={saving}
            data-testid="ai-settings-digest-save"
            className="h-9 px-3 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar
          </button>
          <button type="button" onClick={sendNow} disabled={sending}
            data-testid="ai-settings-digest-send-now"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold text-white hover:border-emerald-500/60 inline-flex items-center gap-1.5 disabled:opacity-50">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Enviar agora
          </button>
        </div>
      </section>

      {preview && (
        <section className="rounded-2xl border border-white/10 p-4 space-y-2"
                  data-testid="ai-settings-digest-preview">
          <h3 className="text-[12px] uppercase tracking-widest font-bold text-slate-300">
            Preview ({preview.window_days} dias)
          </h3>
          <div className="text-[11.5px] text-slate-400">
            {preview.stats.runs} runs · ${preview.stats.cost_usd_total} USD ·
            {" "}{preview.stats.web_search_calls} web searches ·
            {" "}{preview.stats.cache_hits} cache hits
          </div>
          <div className="text-[13px] text-white font-bold">Top {preview.top_leads.length} leads</div>
          <ol className="space-y-1 text-[11.5px] text-slate-300 list-decimal list-inside">
            {preview.top_leads.map((l) => (
              <li key={l.id}>
                <strong className="text-white">{l.name}</strong> · Tier {l.opportunity_tier} {l.opportunity_score} · conf {l.confidence_overall}%
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  ICP tab
// ═════════════════════════════════════════════════════════════════════════

const ICP_DIMENSIONS = [
  { key: "facility_footprint",  label: "Facility footprint" },
  { key: "company_size",         label: "Company size" },
  { key: "operations_247",       label: "24/7 operations" },
  { key: "security_indicators",  label: "Security indicators" },
  { key: "buying_signals",       label: "Buying signals" },
  { key: "decision_makers",      label: "Decision makers" },
  { key: "procurement_access",   label: "Procurement access" },
  { key: "vendor_displacement",  label: "Vendor displacement" },
  { key: "geography_fit",        label: "Geography fit" },
  { key: "evidence_quality",     label: "Evidence quality" },
];

function IcpTab() {
  const [profiles, setProfiles] = useState([]);
  const [defaultWeights, setDefaultWeights] = useState({});
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ai-search/icp-profiles`, authHeader);
      setProfiles(r.data?.profiles || []);
      setDefaultWeights(r.data?.default_weights || {});
    } catch (ex) {
      toast.error("Falha ao carregar profiles");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line

  const startNew = () => setEditing({
    id: null, name: "Novo profile", description: "", industry_focus: [],
    is_default: false, weights: { ...defaultWeights },
  });
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/ai-search/icp-profiles`, editing, authHeader);
      toast.success(`Profile "${r.data.name}" salvo`);
      setEditing(null);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao salvar profile");
    } finally { setSaving(false); }
  };
  const remove = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Deletar profile "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/ai-search/icp-profiles/${p.id}`, authHeader);
      toast.success("Deletado");
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.message || ex?.response?.data?.detail || "Falha ao deletar");
    }
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-purple-400 mx-auto" /></div>;

  const total = editing ? Object.values(editing.weights || {}).reduce((s, v) => s + Number(v || 0), 0) : 100;

  return (
    <div className="space-y-4" data-testid="ai-settings-icp">
      <section className="flex items-center justify-between">
        <h3 className="text-[12px] uppercase tracking-widest font-bold text-slate-300 flex items-center gap-2">
          <Sliders className="w-3.5 h-3.5" /> ICP Scoring Profiles
        </h3>
        <button type="button" onClick={startNew}
          data-testid="ai-settings-icp-new"
          className="h-8 px-2.5 rounded-lg text-[11px] font-bold text-white inline-flex items-center gap-1.5"
          style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
          <Plus className="w-3 h-3" /> Novo Profile
        </button>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {profiles.map((p) => (
          <div key={p.id} data-testid={`ai-settings-icp-profile-${p.id}`}
            className="rounded-2xl border border-white/10 p-3 space-y-2">
            <header className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[13px] font-bold text-white">
                  {p.name} {p.is_default && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">DEFAULT</span>}
                </div>
                <div className="text-[10.5px] text-slate-400">{p.description || "—"}</div>
                <div className="text-[10px] text-slate-500 mt-1">
                  {(p.industry_focus || []).join(", ") || "sem setor"}
                </div>
              </div>
              <div className="flex gap-1">
                <button type="button" onClick={() => setEditing({ ...p })}
                  data-testid={`ai-settings-icp-edit-${p.id}`}
                  className="h-7 px-2 rounded border border-white/15 text-[10.5px] text-slate-200 hover:border-purple-500/60">
                  Editar
                </button>
                {!p.is_default && (
                  <button type="button" onClick={() => remove(p)}
                    data-testid={`ai-settings-icp-delete-${p.id}`}
                    className="h-7 w-7 rounded border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 inline-flex items-center justify-center">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </header>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10.5px]">
              {ICP_DIMENSIONS.map((d) => (
                <div key={d.key} className="flex items-center justify-between">
                  <span className="text-slate-400 truncate mr-1">{d.label}</span>
                  <span className="text-white font-bold tabular-nums">{p.weights?.[d.key] ?? 0}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <section className="rounded-2xl border border-purple-500/30 p-4 space-y-3"
                  style={{ background: "rgba(124,58,237,0.05)" }}
                  data-testid="ai-settings-icp-editor">
          <div className="flex items-center justify-between">
            <h4 className="text-[13px] font-bold text-white">Editando: {editing.name}</h4>
            <button type="button" onClick={() => setEditing(null)}
              className="text-[11px] text-slate-400 hover:text-white">✕ Cancelar</button>
          </div>
          <input type="text" value={editing.name}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            data-testid="ai-settings-icp-name-input"
            placeholder="Nome do profile"
            className="h-9 px-2 rounded border border-white/15 bg-black/30 text-white w-full text-[12.5px]" />
          <textarea value={editing.description || ""}
            onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            data-testid="ai-settings-icp-desc-input"
            placeholder="Descrição curta"
            rows={2}
            className="px-2 py-1.5 rounded border border-white/15 bg-black/30 text-white w-full text-[11.5px]" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ICP_DIMENSIONS.map((d) => (
              <div key={d.key} className="flex items-center gap-2">
                <span className="text-[11.5px] text-slate-300 flex-1">{d.label}</span>
                <input type="range" min={0} max={30} step={1}
                  value={editing.weights?.[d.key] ?? 0}
                  onChange={(e) => setEditing({ ...editing,
                    weights: { ...editing.weights, [d.key]: Number(e.target.value) } })}
                  data-testid={`ai-settings-icp-weight-${d.key}`}
                  className="flex-1 accent-purple-500" />
                <span className="text-[11.5px] font-bold text-white w-8 text-right tabular-nums">
                  {editing.weights?.[d.key] ?? 0}
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 text-[11px]">
            <span className="text-slate-400">Soma: <b className={total === 100 ? "text-emerald-300" : "text-amber-300"}>{total}</b></span>
            <span className="text-slate-500">{total !== 100 && "(auto-normalizado ao salvar)"}</span>
            <label className="ml-auto flex items-center gap-1.5 text-slate-300">
              <input type="checkbox" checked={editing.is_default}
                onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })}
                data-testid="ai-settings-icp-default-toggle" />
              Marcar como default
            </label>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={saving}
              data-testid="ai-settings-icp-save"
              className="h-9 px-3 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Salvar profile
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
